import { useEffect, useMemo } from 'react'
import { useApp, useNow } from './store/AppContext'
import { AuthScreen } from './screens/AuthScreen'
import { SetPasswordScreen } from './screens/SetPasswordScreen'
import { WorkspaceGate } from './screens/WorkspaceGate'
import { TimerScreen } from './screens/TimerScreen'
import { TimesheetScreen } from './screens/TimesheetScreen'
import { ReportsScreen } from './screens/ReportsScreen'
import { ProjectsScreen } from './screens/ProjectsScreen'
import { ClientsScreen } from './screens/ClientsScreen'
import { TeamScreen } from './screens/TeamScreen'
import { SettingsScreen } from './screens/SettingsScreen'
import { PageHead, Sidebar, Toast, Topnav, visibleScreens } from './components/Chrome'
import { ModalHost } from './components/ModalHost'
import { formatDur, todayKey } from './lib/time'

export default function App() {
  const now = useNow()
  const { booting, auth, ws, me, role, screen, setScreen, running, myWeek, loadError, recovery } =
    useApp()

  /* Keep the tab title useful while a timer runs. */
  useEffect(() => {
    if (!running) {
      document.title = 'Cadence — Time tracking for teams'
      return
    }
    const sec = Math.max(0, Math.floor((now - running.at) / 1000))
    const h = Math.floor(sec / 3600)
    const m = Math.floor((sec % 3600) / 60)
    document.title = `${h}:${String(m).padStart(2, '0')} · ${running.desc || 'Tracking'} — Cadence`
  }, [running, now])

  /* A role change elsewhere can strip access to the screen you're on. */
  const allowed = useMemo(
    () => !role || visibleScreens(role).some(([id]) => id === screen),
    [role, screen],
  )
  useEffect(() => {
    if (!allowed) setScreen('timer')
  }, [allowed, setScreen])

  const totals = useMemo(() => {
    if (!ws || !me) return { today: '0:00:00', week: '0:00:00' }
    const week = myWeek(0)
    const tk = todayKey()
    const mine = ws.entries.filter((e) => e.userId === me.id)
    const todaySec = mine.filter((e) => e.day === tk).reduce((a, e) => a + e.dur, 0)
    const weekSec = mine.filter((e) => week.includes(e.day)).reduce((a, e) => a + e.dur, 0)
    const live = running ? Math.max(0, Math.floor((now - running.at) / 1000)) : 0
    const liveToday = running && new Date(running.at).toDateString() === new Date().toDateString() ? live : 0
    return {
      today: formatDur(todaySec + liveToday, ws.settings.durFormat),
      week: formatDur(weekSec + live, ws.settings.durFormat),
    }
  }, [ws, me, myWeek, running, now])

  if (booting) return <Splash label="Loading…" />
  // Before !auth: a recovery link has already created a session, so the
  // person would otherwise sail straight into the app without ever choosing
  // the password they came here to set.
  if (recovery) return <SetPasswordScreen />
  if (!auth) return <AuthScreen />
  if (!auth.workspaces.length) return <WorkspaceGate />
  if (!ws || !me) {
    return (
      <>
        <Splash label={loadError ?? 'Opening workspace…'} error={!!loadError} />
        <Toast />
      </>
    )
  }

  return (
    /* On the prospect screen the whole shell takes the other accent: the nav
       says which page you are on, and the modals opened from it -- which
       render here rather than inside <main> -- match the page that opened
       them. */
    <div className="shell" data-scope={screen === 'prospects' ? 'prospect' : undefined}>
      {ws.appearance.navLayout === 'topnav' ? <Topnav weekTotal={totals.week} /> : <Sidebar weekTotal={totals.week} />}

      <main className="main">
        <PageHead today={totals.today} week={totals.week} />
        {!allowed ? null : screen === 'timer' ? (
          <TimerScreen kind="client" />
        ) : screen === 'prospects' ? (
          /* The same screen, pointed at the other population. See TimerScreen. */
          <TimerScreen kind="prospect" />
        ) : screen === 'timesheet' ? (
          <TimesheetScreen />
        ) : screen === 'reports' ? (
          <ReportsScreen />
        ) : screen === 'projects' ? (
          <ProjectsScreen />
        ) : screen === 'clients' ? (
          <ClientsScreen />
        ) : screen === 'team' ? (
          <TeamScreen />
        ) : (
          <SettingsScreen />
        )}
      </main>

      <ModalHost />
      <Toast />
    </div>
  )
}

function Splash({ label, error }: { label: string; error?: boolean }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        background: 'var(--canvas)',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <div className="brandmark brandmark--lg">C</div>
      <div style={{ color: error ? 'var(--dg)' : 'var(--mut)', fontSize: 14, maxWidth: 420 }} className="pretty">
        {label}
      </div>
    </div>
  )
}
